import { getClosedWidgetEditState } from '../helpers/webDashboardEditState';
import { normalizeChartCurves } from '../../../utils/chartAggregation';

export const saveWidgetSettings = ({
	dashboard,
	devices,
	validDatastreams,
	findDatastreamByReference,
	normalizeDatastreamPin,
	edit,
	sanitizeWidgetFontSizeProps,
	cloneDashboardValue,
	stripDatastreamManagedProps,
	showFreeGeometrySettings,
	getWidgetRectPx,
	isPercentageLayout,
	geometryWidthValue,
	geometryHeightValue,
	geometryLeftValue,
	geometryTopValue,
	geometryOriginLeft,
	geometryOriginTop,
	roundGeometryValue,
	toLengthInputNumber,
	constrainRectToBackgroundImage,
	isWidgetFreeMovementEnabled,
	isWaterNetworkWidgetType,
	snapWaterNetworkMoveRect,
	checkOverlap,
	getNearestGridRect,
	findNearestEmptySpotForWidgets,
	storeWidgetPosition,
	setDashboard,
	clearWidgetLivePropsById,
	setAlertMessage,
	t,
	setAlertType,
	setShowAlert,
	setEdit,
	getEditableMetrics,
	serializeRectForStorage,
}) => {

									const currentWidgets = dashboard?.widgets || [];
									const metrics = getEditableMetrics();
									const editDatastreamSource = devices?.length > 0
										? ((((devices || []).find(device => Number(device?.id) === Number(edit.deviceId)))?.template?.datastreams || []).filter(ds => ds))
										: validDatastreams;
									const matchedEditDatastream = findDatastreamByReference(
										editDatastreamSource,
										edit.dataId,
										edit.dataPin,
									);
									const resolvedEditDataId = matchedEditDatastream?.id ?? edit.dataId ?? 0;
									const resolvedEditDataPin = normalizeDatastreamPin(
										matchedEditDatastream?.pin ?? edit.dataPin,
									);
									let geometryConflict = false;

									const updatedWidgets = currentWidgets.map((wd) => {
										if (wd.id !== edit.id) {
											return wd;
										}

										let newProps = sanitizeWidgetFontSizeProps(cloneDashboardValue(edit.props || {}));
										if (edit.type === 'chart' && Array.isArray(newProps.curves)) {
											newProps.curves = normalizeChartCurves(newProps.curves);
										}
										if (edit.type === 'camera-stream') {
											delete newProps.themeMode;
										}
										Object.keys(newProps).forEach((k) => {
											if (typeof (wd.extra_props?.[k]) === 'number' && !['speed'].includes(k)) {
												if (!isNaN(parseFloat(newProps[k])) && isFinite(newProps[k])) {
													newProps[k] = parseFloat(newProps[k]);
												}
											}
										});

										if (Number(edit.dataId) > 0) {
											newProps = stripDatastreamManagedProps(newProps, edit.type);
										}

										const nextTitle = edit.type === 'text-title'
											? String(edit.props?.title ?? edit.title ?? wd.title ?? '').trim()
											: edit.title.replace(/^\[.*?\]\s*/, '').trim();

										if (edit.type === 'text-title' && newProps.title == null) {
											newProps.title = nextTitle;
										}

										let nextWidget = {
											...wd,
											extra_props: newProps,
											title: nextTitle,
											transparentBackground: edit.transparentBackground,
											data_id: resolvedEditDataId,
											data_pin: resolvedEditDataPin,
											...(devices?.length > 0 && { device_id: edit.deviceId })
										};
										const disabledFreeMovement = (
											isWidgetFreeMovementEnabled(wd) &&
											!isWidgetFreeMovementEnabled(nextWidget)
										);

										if (showFreeGeometrySettings) {
											const currentRect = getWidgetRectPx(wd, metrics);
											const canvasWidth = Number.isFinite(metrics?.width) ? metrics.width : null;
											const canvasHeight = Number.isFinite(metrics?.height) ? metrics.height : null;

											const nextWidthPx = roundGeometryValue(Math.max(
												1,
												isPercentageLayout
													? geometryWidthValue
													: toLengthInputNumber(edit.width, currentRect.width),
											));
											const nextHeightPx = roundGeometryValue(Math.max(
												1,
												isPercentageLayout
													? geometryHeightValue
													: toLengthInputNumber(edit.height, currentRect.height),
											));

											let nextLeftPx = roundGeometryValue(Math.max(
												0,
												isPercentageLayout
													? geometryOriginLeft + geometryLeftValue
													: toLengthInputNumber(edit.left, currentRect.left),
											));
											let nextTopPx = roundGeometryValue(Math.max(
												0,
												isPercentageLayout
													? geometryOriginTop + geometryTopValue
													: toLengthInputNumber(edit.top, currentRect.top),
											));

											if (canvasWidth !== null) {
												const maxLeft = Math.max(0, canvasWidth - nextWidthPx);
												nextLeftPx = Math.min(nextLeftPx, maxLeft);
											}
											if (canvasHeight !== null) {
												const maxTop = Math.max(0, canvasHeight - nextHeightPx);
												nextTopPx = Math.min(nextTopPx, maxTop);
											}
											const constrainedRect = constrainRectToBackgroundImage(
												nextLeftPx,
												nextTopPx,
												nextWidthPx,
												nextHeightPx,
												currentWidgets,
												wd.id,
											);
											const candidateRect = constrainedRect.constrained
												? constrainedRect
												: {
													left: nextLeftPx,
													top: nextTopPx,
													width: nextWidthPx,
													height: nextHeightPx,
												};
											const adjustedCandidateRect = isWidgetFreeMovementEnabled(wd) && isWaterNetworkWidgetType(wd)
												? snapWaterNetworkMoveRect(
													candidateRect,
													wd,
													currentWidgets,
													getWidgetRectPx,
													metrics,
													wd.id,
												)
												: candidateRect;

											const wouldOverlap = checkOverlap(
												adjustedCandidateRect.left,
												adjustedCandidateRect.top,
												adjustedCandidateRect.width,
												adjustedCandidateRect.height,
												currentWidgets,
												wd.id,
												wd,
											);

											if (wouldOverlap) {
												geometryConflict = true;
											} else {
												nextWidget = {
													...nextWidget,
													...(isPercentageLayout
														? serializeRectForStorage(adjustedCandidateRect, metrics)
														: {
															width: adjustedCandidateRect.width,
															height: adjustedCandidateRect.height,
															left: adjustedCandidateRect.left,
															top: adjustedCandidateRect.top,
														}),
												};
											}
										}

										if (disabledFreeMovement) {
											const sourceRect = getWidgetRectPx(nextWidget, metrics);
											let gridRect = getNearestGridRect(sourceRect, metrics);
											const hasConflictAtNearestCell = checkOverlap(
												gridRect.left,
												gridRect.top,
												gridRect.width,
												gridRect.height,
												currentWidgets,
												wd.id,
												nextWidget,
											);

											if (hasConflictAtNearestCell) {
												const fallbackSpot = findNearestEmptySpotForWidgets(
													gridRect.left,
													gridRect.top,
													gridRect.width,
													gridRect.height,
													currentWidgets.filter((widget) => widget?.id !== wd.id),
												);

												if (fallbackSpot.found) {
													gridRect = {
														...gridRect,
														left: fallbackSpot.left,
														top: fallbackSpot.top,
													};
												} else {
													geometryConflict = true;
													newProps = {
														...newProps,
														freeMovement: true,
													};
													nextWidget = {
														...nextWidget,
														extra_props: newProps,
													};
												}
											}

											if (!isWidgetFreeMovementEnabled(nextWidget)) {
												nextWidget = storeWidgetPosition(nextWidget, gridRect, metrics);
											}
										}

										return nextWidget;
									});

									setDashboard({
										...dashboard,
										widgets: updatedWidgets
									});
									clearWidgetLivePropsById(edit.id);

									if (geometryConflict) {
										setAlertMessage(
											t.noOverlapOnBackgroundImage ||
											'Widgets cannot overlap, even on top of a background image.'
										);
										setAlertType('warning');
										setShowAlert(true);
									}

									setEdit(getClosedWidgetEditState());
};
